import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Sidebar, ToastContent } from "../../shared/components";
import { LoadingComp } from "../../shared/components/loading/Loading";
import { AuthGuard } from "../../shared/guards/AuthGuard";
import { useToast } from "../../shared/hooks";
import { CapitalizationService } from "../../shared/services/api/capitalization/CapitalizationService";
import { globalRemoveMaskReal2 } from "../../shared/utils";
import { CapitalizationForm } from "./components/CapitalizationForm";

export const CapitalizationDetailPage = (): JSX.Element => {
    const navigate = useNavigate();
    const toast = useToast();
    const { id = "novo" } = useParams<"id">();
    const [isLoading, setIsLoading] = useState(false);

    // Dados iniciais vazios ou padrão para criação
    const defaultData = useMemo(() => ({
        realEstateId: "",
        clientId: "",
        insuranceCompany: "",
        status: "issuing", // Default to English value

        ownerDocument: "",
        ownerName: "",
        ownerEmail: "",
        ownerPhone: "",

        rentAmount: "0",
        condominiumAmount: "0",
        waterAmount: "0",
        energyAmount: "0",
        gasAmount: "0",
        iptuAmount: "0",
        startDate: new Date().toISOString().split("T")[0],
        endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split("T")[0],
        plan: "12",
        multiplier: "1",
        titleAmount: "0",
        titleNumber: "",
        proposalNumber: "",
        issueDate: new Date().toISOString().split("T")[0],

        propertyZipcode: "",
        propertyStreet: "",
        propertyNumber: "",
        propertyComplement: "",
        propertyDistrict: "",
        propertyCity: "",
        propertyState: "",
        propertyType: "",
        propertyRentalType: "",

        tenantDocument: "",
        tenantName: "",
        tenantGender: "",
        tenantBirthDate: "",
        tenantEmail: "",
        tenantPhone: "",
        tenantMaritalStatus: "",
        tenantNationality: "",
        tenantIdentity: "",
        tenantIssuer: "",
        tenantState: "",
        tenantIssueDate: "",
        tenantProfession: "",
        tenantMonthlyIncome: "R2500",
        tenantPpe: false,
    }), []);

    const [formData, setFormData] = useState<any>(defaultData);

    useEffect(() => {
        if (id !== "novo") {
            setIsLoading(true);
            CapitalizationService.getById(id)
                .then((result) => {
                    setIsLoading(false);
                    if (result instanceof Error) {
                        toast.present({
                            durationMs: 3000,
                            type: "error",
                            position: "top-right",
                            messageOrContent: <ToastContent title="Erro" content={result.message} />,
                        });
                        navigate("/capitalizacao");
                    } else {
                        setFormData({ ...result, clientId: result.realEstateId });
                    }
                });
        } else {
            setFormData(defaultData);
        }
    }, [id, defaultData, navigate]);

    const handleSave = async (data: any) => {
        try {
            const payload = {
                ...data,
                realEstateId: data.clientId || data.realEstateId, // Ensure realEstateId is set
                rentAmount: globalRemoveMaskReal2(data.rentAmount),
                condominiumAmount: globalRemoveMaskReal2(data.condominiumAmount),
                waterAmount: globalRemoveMaskReal2(data.waterAmount),
                energyAmount: globalRemoveMaskReal2(data.energyAmount),
                gasAmount: globalRemoveMaskReal2(data.gasAmount),
                iptuAmount: globalRemoveMaskReal2(data.iptuAmount),
                titleAmount: globalRemoveMaskReal2(data.titleAmount),
            };
            delete payload.clientId;

            let result;
            if (id === "novo") {
                result = await CapitalizationService.create(payload);
            } else {
                result = await CapitalizationService.update(id, payload);
            }

            if (result instanceof Error) {
                toast.present({
                    durationMs: 3000,
                    type: "error",
                    position: "top-right",
                    messageOrContent: <ToastContent title="Erro" content={result.message} />,
                });
            } else {
                toast.present({
                    durationMs: 3000,
                    type: "success",
                    position: "top-right",
                    messageOrContent: <ToastContent title="Sucesso" content={id === "novo" ? "Capitalização criada com sucesso!" : "Capitalização atualizada com sucesso!"} />,
                });
                navigate("/capitalizacao");
            }
        } catch (error: any) {
            toast.present({
                durationMs: 3000,
                type: "error",
                position: "top-right",
                messageOrContent: <ToastContent title="Erro" content="Erro ao salvar capitalização." />,
            });
        }
    };

    return (
        <AuthGuard>
            <Sidebar>
                <LoadingComp isopen={isLoading} />
                {!isLoading && (
                    <CapitalizationForm
                        initialData={formData}
                        onSubmit={handleSave}
                    />
                )}
            </Sidebar>
        </AuthGuard>
    );
};
